/**
 * Sync engine (spec §3.2): outbox push + seq-cursor pull against the Pages
 * Functions API, with poll/visibility/online/debounce triggers and
 * exponential backoff.
 *
 * Framework-free: no Svelte (or any UI) imports. The UI layer owns the
 * lifecycle (start/stop) and consumes 'status' / 'pagechanged' events.
 */
import { b64ToBytes, bytesToB64, type Mutation, type StrokeMeta } from './protocol';
import type { RemoteImage, RemoteNotebook, RemotePage, RemotePurge, RemoteStroke, RemoteTextBox } from '../store/repo';
import type { Image, Notebook, Page, TextBox } from '../store/types';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

/** Wire shape of POST /api/ocr's 200 responses (see functions/api/router.ts handleOcr). */
export type OcrResponse = { ok: true } | { skipped: 'rev' | 'quota' };

/** One matched OCR word: `w` = word text, `r` = [x, y, w, h] in WORLD coords. */
export interface SearchBox {
  w: string;
  r: [number, number, number, number];
}

/** One row of GET /api/search (see functions/api/router.ts handleSearch). */
export interface SearchResultRow {
  pageId: string;
  kind: 'ocr' | 'text';
  /** FTS5 snippet with literal `**…**` around matches — escape before rendering. */
  snippet: string;
  /** Present on ocr rows only: the page's OCR words matching a query term. */
  boxes?: SearchBox[];
}

export interface SearchResponse {
  results: SearchResultRow[];
}

/** The slice of Repo the engine depends on (structural, so tests can stub it). */
export interface SyncRepo {
  onMutation?: () => void;
  getOutboxBatch(limit: number): Promise<{ key: number; mutation: Mutation }[]>;
  deleteOutbox(keys: number[]): Promise<void>;
  getSyncMeta(key: string): Promise<unknown>;
  setSyncMeta(key: string, value: unknown): Promise<void>;
  applyRemoteNotebook(record: RemoteNotebook): Promise<void>;
  applyRemotePage(record: RemotePage): Promise<void>;
  applyRemoteStroke(record: RemoteStroke): Promise<void>;
  applyRemoteTextBox(record: RemoteTextBox): Promise<void>;
  applyRemoteImage(record: RemoteImage): Promise<void>;
  /** Hard-delete cascade + outbox scrub; resolves to the affected local page ids. */
  applyRemotePurge(purge: RemotePurge): Promise<string[]>;
}

export interface SyncEngineOpts {
  /** API origin; '' = same-origin (production). */
  baseUrl?: string;
  /** Poll interval while visible. Overridable for tests / ?syncPoll. */
  pollMs?: number;
  /** Injectable fetch for tests. */
  fetchFn?: typeof fetch;
  /** Token override for tests; normally read from syncmeta at start(). */
  token?: string;
}

export interface SyncEngineEvents {
  status: (status: SyncStatus) => void;
  pagechanged: (pageIds: Set<string>) => void;
  /**
   * Fired once per sync cycle when the pull applied ≥1 row of ANY kind.
   * Unlike pagechanged, this also covers notebook-only changes (create /
   * rename / delete on another device), which touch no pageId — the Library
   * listens to refresh its list.
   */
  applied: () => void;
  /**
   * Fired once per applied remote purge ROW, BEFORE pagechanged, so the
   * Editor can bail out of a notebook/page that just ceased to exist instead
   * of reacting to pagechanged by reloading it.
   *
   * NOT once per user action: a notebook purge arrives as N page rows plus
   * one notebook row (all one seq group), so it fires N+1 times. Rows can
   * also repeat across pagination pages (purges ride whole with their seq
   * group when stroke pagination pulls the cursor back) and on self-echoes
   * (a purge this device pushed comes back in its own pull). Consumers MUST
   * be idempotent — treat "already gone" as success.
   */
  purged: (purge: RemotePurge) => void;
  /**
   * Fired at most ONCE per sync cycle, right after the outbox fully drains,
   * with the pageIds affected by the successfully pushed mutations — stroke/
   * textbox/image pageIds plus page-mutation ids. Notebook mutations and
   * purges are excluded (no page content to OCR; purged pages are gone).
   *
   * Per-cycle, not per-batch: a multi-batch drain emits one union set. If a
   * later batch fails mid-cycle, the cycle emits nothing even though earlier
   * batches landed — consumers (the M5 OCR scheduler) self-heal on the next
   * edit, and the server's rev-skip makes eventual repeats free.
   */
  pushed: (pageIds: Set<string>) => void;
}

const PUSH_BATCH = 200;
const DEBOUNCE_MS = 2000;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 120_000;

/** Wire shape of GET /api/changes (see functions/api/router.ts handleChanges). */
interface ChangesResponse {
  seq: number;
  more: boolean;
  notebooks: Array<{ id: string; data: unknown; updatedAt: number; deletedAt: number | null; seq: number }>;
  pages: Array<{ id: string; notebookId: string; data: unknown; rev: number; updatedAt: number; deletedAt: number | null; seq: number }>;
  strokes: Array<{ id: string; pageId: string; meta: unknown; pointsB64: string; deletedAt: number | null; seq: number }>;
  /** Optional so a client updated ahead of the server keeps syncing (M3b additions). */
  textboxes?: Array<{ id: string; pageId: string; data: unknown; updatedAt: number; deletedAt: number | null; seq: number }>;
  /** Optional for the same reason (M4 addition). */
  images?: Array<{ id: string; pageId: string; data: unknown; updatedAt: number; deletedAt: number | null; seq: number }>;
  purges?: Array<{ target: 'notebook' | 'page'; id: string; seq: number }>;
}

function mintDeviceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Array.from({ length: 4 }, () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0')).join('');
}

export class SyncEngine {
  private readonly baseUrl: string;
  private readonly pollMs: number;
  private readonly fetchFn: typeof fetch;
  private readonly tokenOverride?: string;

  private token = '';
  private deviceId = '';
  private lastServerSeq = 0;
  private loaded: Promise<void> | null = null;

  private inflight: Promise<void> | null = null;
  private failures = 0;
  private backoffUntil = 0;

  private statusValue: SyncStatus = 'idle';
  private listeners: { [K in keyof SyncEngineEvents]?: SyncEngineEvents[K][] } = {};

  private started = false;
  private abort: AbortController | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private hook: (() => void) | null = null;

  constructor(private repo: SyncRepo, opts: SyncEngineOpts = {}) {
    this.baseUrl = opts.baseUrl ?? '';
    this.pollMs = opts.pollMs ?? 15_000;
    // Wrap rather than alias: a bare `fetch` reference loses its window binding.
    this.fetchFn = opts.fetchFn ?? ((input, init) => fetch(input, init));
    this.tokenOverride = opts.token;
  }

  // ---- events ----
  on<K extends keyof SyncEngineEvents>(ev: K, cb: SyncEngineEvents[K]): void {
    ((this.listeners[ev] ??= []) as SyncEngineEvents[K][]).push(cb);
  }
  off<K extends keyof SyncEngineEvents>(ev: K, cb: SyncEngineEvents[K]): void {
    const arr = this.listeners[ev] as SyncEngineEvents[K][] | undefined;
    if (!arr) return;
    const i = arr.indexOf(cb);
    if (i !== -1) arr.splice(i, 1);
  }
  private emit<K extends keyof SyncEngineEvents>(ev: K, ...args: Parameters<SyncEngineEvents[K]>): void {
    for (const cb of this.listeners[ev] ?? []) {
      try {
        (cb as (...a: Parameters<SyncEngineEvents[K]>) => void)(...args);
      } catch {
        // Listener errors must never break the sync loop.
      }
    }
  }

  get status(): SyncStatus {
    return this.statusValue;
  }
  private setStatus(s: SyncStatus): void {
    if (this.statusValue === s) return;
    this.statusValue = s;
    this.emit('status', s);
  }

  // ---- lifecycle ----
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    await this.ensureLoaded();

    this.hook = () => this.scheduleDebounce();
    this.repo.onMutation = this.hook;

    this.interval = setInterval(() => {
      if (this.isVisible()) this.autoSync();
    }, this.pollMs);

    this.abort = new AbortController();
    const signal = this.abort.signal;
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'visible') this.autoSync();
        },
        { signal },
      );
    }
    if (typeof window !== 'undefined') {
      // Coming back online is a fresh network: drop any backoff and retry now.
      window.addEventListener(
        'online',
        () => {
          this.resetBackoff();
          void this.runSync();
        },
        { signal },
      );
    }

    void this.runSync(); // immediate kick; failures feed the backoff as usual
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.abort?.abort();
    this.abort = null;
    if (this.interval !== null) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }
    if (this.repo.onMutation === this.hook) this.repo.onMutation = undefined;
    this.hook = null;
  }

  /** Manual sync: resets any backoff, coalesces with an in-flight run. */
  syncNow(): Promise<void> {
    this.resetBackoff();
    return this.runSync();
  }

  // ---- triggers ----
  private isVisible(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible';
  }

  /** Automatic trigger: respects the backoff window (manual syncNow does not). */
  private autoSync(): void {
    if (Date.now() < this.backoffUntil) return;
    void this.runSync();
  }

  private scheduleDebounce(): void {
    if (this.debounce !== null) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => {
      this.debounce = null;
      this.autoSync();
    }, DEBOUNCE_MS);
  }

  private resetBackoff(): void {
    this.failures = 0;
    this.backoffUntil = 0;
  }

  // ---- sync cycle ----
  private ensureLoaded(): Promise<void> {
    return (this.loaded ??= (async () => {
      this.token = this.tokenOverride ?? ((await this.repo.getSyncMeta('syncToken')) as string | undefined) ?? '';
      this.lastServerSeq = ((await this.repo.getSyncMeta('lastServerSeq')) as number | undefined) ?? 0;
      let deviceId = (await this.repo.getSyncMeta('deviceId')) as string | undefined;
      if (!deviceId) {
        deviceId = mintDeviceId();
        await this.repo.setSyncMeta('deviceId', deviceId);
      }
      this.deviceId = deviceId;
    })());
  }

  /** Single-flight: a call while a sync is running returns the running promise. */
  private runSync(): Promise<void> {
    if (this.inflight) return this.inflight;
    this.inflight = this.doSync().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async doSync(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setStatus('offline');
      return;
    }
    await this.ensureLoaded();
    this.setStatus('syncing');
    try {
      const pushedIds = await this.push();
      // Emitted before the pull so a pull failure can't suppress a push that
      // DID land (the event drives OCR scheduling, not UI refresh).
      if (pushedIds.size > 0) this.emit('pushed', pushedIds);
      const { changed, appliedRows, purges } = await this.pull();
      this.resetBackoff();
      this.setStatus('idle');
      if (appliedRows > 0) this.emit('applied');
      // purged fires before pagechanged so an Editor showing the purged
      // notebook/page bails out instead of reloading a dead page.
      for (const p of purges) this.emit('purged', p);
      if (changed.size > 0) this.emit('pagechanged', changed);
    } catch {
      this.failures += 1;
      const delay = Math.min(BACKOFF_BASE_MS * 2 ** (this.failures - 1), BACKOFF_CAP_MS);
      this.backoffUntil = Date.now() + delay;
      this.setStatus('error');
    }
  }

  private authHeaders(): Record<string, string> {
    return { Authorization: `Bearer ${this.token}` };
  }

  /**
   * Fetch a stored file's bytes (GET /api/files/<fileId>, bearer-authed) —
   * the fetcher the Editor passes into repo.getFile for R2-backed PDFs and
   * images. Loads the token through the same ensureLoaded path as the sync
   * cycle. Throws when offline or when no sync token exists (sync disabled):
   * in that state files can never be fetched remotely, which is why
   * local-only import/insert flows must repo.putFile at upload time (see
   * Repo.getFile).
   */
  async fetchFile(fileId: string): Promise<ArrayBuffer> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error('offline: cannot fetch file');
    }
    await this.ensureLoaded();
    if (!this.token) throw new Error('no sync token: files cannot be fetched while sync is disabled');
    const res = await this.fetchFn(`${this.baseUrl}/api/files/${fileId}`, {
      headers: this.authHeaders(),
    });
    if (!res.ok) throw new Error(`file fetch failed: ${res.status}`);
    return res.arrayBuffer();
  }

  /**
   * Upload a page's OCR snapshot: POST /api/ocr (bearer-authed) with the PNG
   * blob base64-encoded into the JSON body (chunked encoder, same as stroke
   * points). Resolves the server's 200 body — `{ ok: true }` or
   * `{ skipped: 'rev' | 'quota' }` — and throws on offline / sync disabled /
   * non-2xx; the OCR scheduler treats throws as drop-and-self-heal.
   */
  async postOcr(pageId: string, rev: number, scale: number, png: Blob): Promise<OcrResponse> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error('offline: cannot upload OCR snapshot');
    }
    await this.ensureLoaded();
    if (!this.token) throw new Error('no sync token: OCR requires sync enabled');
    const bytes = new Uint8Array(await png.arrayBuffer());
    const res = await this.fetchFn(`${this.baseUrl}/api/ocr`, {
      method: 'POST',
      headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ pageId, rev, scale, png: bytesToB64(bytes) }),
    });
    if (!res.ok) throw new Error(`ocr post failed: ${res.status}`);
    return (await res.json()) as OcrResponse;
  }

  /**
   * Server-side full-text search: GET /api/search?q= (bearer-authed). Resolves
   * the raw wire response; the UI layer (appState.search) groups rows by
   * notebook from local IDB. Throws on offline / sync disabled / non-2xx —
   * search is server-only, so the SearchBar surfaces those as error states.
   */
  async search(q: string): Promise<SearchResponse> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new Error('offline: cannot search');
    }
    await this.ensureLoaded();
    if (!this.token) throw new Error('no sync token: search requires sync enabled');
    const res = await this.fetchFn(`${this.baseUrl}/api/search?q=${encodeURIComponent(q)}`, {
      headers: this.authHeaders(),
    });
    if (!res.ok) throw new Error(`search failed: ${res.status}`);
    return (await res.json()) as SearchResponse;
  }

  /**
   * Drain the outbox oldest-first in ≤200-mutation batches; rows are deleted
   * only after a 2xx. Returns the pageIds affected by the pushed mutations
   * (for the `pushed` event — see SyncEngineEvents.pushed for the contract).
   */
  private async push(): Promise<Set<string>> {
    const pushedIds = new Set<string>();
    for (;;) {
      const batch = await this.repo.getOutboxBatch(PUSH_BATCH);
      if (batch.length === 0) return pushedIds;
      const res = await this.fetchFn(`${this.baseUrl}/api/push`, {
        method: 'POST',
        headers: { ...this.authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: this.deviceId, mutations: batch.map((b) => b.mutation) }),
      });
      if (!res.ok) throw new Error(`push failed: ${res.status}`);
      await this.repo.deleteOutbox(batch.map((b) => b.key));
      for (const { mutation: m } of batch) {
        if (m.kind === 'page') pushedIds.add(m.id);
        else if (m.kind === 'stroke' || m.kind === 'textbox' || m.kind === 'image') pushedIds.add(m.pageId);
      }
    }
  }

  /**
   * Pull pages of changes while the server reports `more`, applying
   * notebooks → pages → textboxes → images → strokes (dependency order) and
   * purges LAST — a purge must win over any upsert that rode in the same
   * batch — persisting the cursor after each fully-applied page. Returns the
   * set of affected pageIds plus the total row count applied (notebook rows
   * have no pageId, so `changed` alone can't tell "nothing happened" from
   * "notebook-only change") plus the applied purges (for the purged event).
   * Textboxes/images/purges may repeat across pagination pages (they ride
   * whole with their seq group) — application is idempotent, so repeats are
   * harmless.
   */
  private async pull(): Promise<{ changed: Set<string>; appliedRows: number; purges: RemotePurge[] }> {
    const changed = new Set<string>();
    const purges: RemotePurge[] = [];
    let appliedRows = 0;
    for (;;) {
      const res = await this.fetchFn(`${this.baseUrl}/api/changes?since=${this.lastServerSeq}`, {
        headers: this.authHeaders(),
      });
      if (!res.ok) throw new Error(`changes failed: ${res.status}`);
      const body = (await res.json()) as ChangesResponse;

      for (const row of body.notebooks) {
        await this.repo.applyRemoteNotebook({
          ...(row.data as Notebook),
          id: row.id,
          updatedAt: row.updatedAt,
          deletedAt: row.deletedAt,
        });
      }
      for (const row of body.pages) {
        await this.repo.applyRemotePage({
          ...(row.data as Page),
          id: row.id,
          notebookId: row.notebookId,
          rev: row.rev,
          updatedAt: row.updatedAt,
          deletedAt: row.deletedAt,
        });
        changed.add(row.id);
      }
      for (const row of body.textboxes ?? []) {
        await this.repo.applyRemoteTextBox({
          ...(row.data as TextBox),
          id: row.id,
          pageId: row.pageId,
          updatedAt: row.updatedAt,
          deletedAt: row.deletedAt,
        });
        changed.add(row.pageId);
      }
      for (const row of body.images ?? []) {
        await this.repo.applyRemoteImage({
          ...(row.data as Image),
          id: row.id,
          pageId: row.pageId,
          updatedAt: row.updatedAt,
          deletedAt: row.deletedAt,
        });
        changed.add(row.pageId);
      }
      for (const row of body.strokes) {
        const meta = row.meta as StrokeMeta;
        await this.repo.applyRemoteStroke({
          id: row.id,
          pageId: row.pageId,
          tool: meta.tool,
          color: meta.color,
          baseWidth: meta.baseWidth,
          bbox: meta.bbox,
          createdAt: meta.createdAt,
          points: b64ToBytes(row.pointsB64).buffer as ArrayBuffer,
          deletedAt: row.deletedAt,
        });
        changed.add(row.pageId);
      }
      // Purges LAST: every upsert from the same batch is already applied, so
      // the hard-delete cascade cannot be resurrected by a row that followed.
      for (const row of body.purges ?? []) {
        const pageIds = await this.repo.applyRemotePurge({ target: row.target, id: row.id });
        for (const pid of pageIds) changed.add(pid);
        purges.push({ target: row.target, id: row.id });
      }

      appliedRows +=
        body.notebooks.length + body.pages.length + body.strokes.length +
        (body.textboxes?.length ?? 0) + (body.images?.length ?? 0) + (body.purges?.length ?? 0);

      if (body.seq > this.lastServerSeq) {
        this.lastServerSeq = body.seq;
        await this.repo.setSyncMeta('lastServerSeq', body.seq);
      }
      if (!body.more) return { changed, appliedRows, purges };
    }
  }
}
